import { literalType, processTerm } from "../../../api/content/processTerms";
import type { ProcessTerm } from "../../../api/generated/processVocabulary";

/**
 * The views the web app has for data types (ADR-0032 D8, types-tooling R2): the one registry
 * every input card, value field and picker resolves through. Each view is registered for the
 * class it was built for; a subclass without its own view is shown with its nearest
 * ancestor's (`resolvePresentation`). A view never changes what an input accepts.
 *
 * - `edit`: the field a value is entered in.
 * - `pick`: the control a value is chosen with; the item kinds it offers are module B's
 *   answer (`PlannedInput.itemKinds`).
 * - `inspect`: how a card shows what fills the input.
 */
export type PresentationOperation = "edit" | "pick" | "inspect";

export type EditView = "checkbox" | "integer" | "number" | "text";
export type PickView = "library" | "llmRoute" | "result";
export type InspectView = "threadBinding" | "item";

export interface ViewOf {
  readonly edit: EditView;
  readonly pick: PickView;
  readonly inspect: InspectView;
}

export interface Presentation<Operation extends PresentationOperation = PresentationOperation> {
  /** The class it was built for. */
  readonly target: string;
  readonly operation: Operation;
  readonly view: ViewOf[Operation];
}

/**
 * Where a type has several nearest ancestors with a view, none of which extends another:
 * the class whose view it takes, by type. Without a preference it takes its base's view,
 * never one by registry order.
 */
export type PresentationPreferences = Readonly<Record<string, string>>;

function registered<Operation extends PresentationOperation, Name extends string>(operation: Operation, iriOf: (name: Name) => string, views: Partial<Readonly<Record<Name, ViewOf[Operation]>>>): Presentation<Operation>[] {
  return (Object.entries(views) as [Name, ViewOf[Operation]][]).map(([name, view]) => ({ target: iriOf(name), operation, view }));
}

/** A step kind (`AgentStepDefinition` …) takes `StepDefinition`'s view through its lineage. */
const ITEM_CLASSES: readonly ProcessTerm[] = ["ItemVersion", "Workflow", "StepDefinition", "Prompt", "Tool", "Skill", "Artifact", "LabelList", "OntologyModule", "ShapesGraph", "CompetencyQuestion", "FewShotExample", "CapabilityOverrides"];
const THREAD_BINDINGS: readonly ProcessTerm[] = ["CorpusEntry", "Document", "Segment", "Corpus", "SourceVersion"];

export const PRESENTATIONS: readonly Presentation[] = [
  ...registered("edit", literalType, { boolean: "checkbox", integer: "integer", decimal: "number", string: "text" }),
  // An ontology slice is a kind of thing written as text: entered in the text field, as before (V2).
  ...registered("edit", processTerm, { OntologySlice: "text" }),
  // An agent step's LLM route (`dmwp:runsWith`, F106) is chosen in the LLM picker; a recorded
  // result among those the person may decide (A8).
  ...registered("pick", processTerm, { ItemVersion: "library", LlmRoute: "llmRoute", RecordedResult: "result" }),
  ...registered("inspect", processTerm, Object.fromEntries([...THREAD_BINDINGS.map((name) => [name, "threadBinding"] as const), ...ITEM_CLASSES.map((name) => [name, "item"] as const)])),
];

/** No type has a preference yet: an ambiguous one takes its base's view. */
export const PRESENTATION_PREFERENCES: PresentationPreferences = {};
